import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { debounceTime, finalize, take } from 'rxjs/operators';
import { NotificationService } from '@core/services/notification/notification.service';
import { CustomValidatorsService } from '@shared/form-elements/custom-validators/custom-validators.service';
import { ReserveInStoreService } from '@features/reserve-in-store/reserve-in-store.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SettingsService } from '@core/services/settings.service';
import { ReserveInStore, ReserveInStoreEvent } from '@features/reserve-in-store/reserve-in-store';
import { StoresService } from '@features/stores-overview/service/stores.service';
import { FindStoreFromId } from '../reserve-in-store-pipes/find-store-from-id.pipe';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { SafePipe } from '@shared/safe/safe.pipe';
import { InterceptLinksDirective } from '@shared/intercept-links/intercept-links.directive';
import { CheckboxComponent } from '@shared/form-elements/checkbox/checkbox.component';
import { InputPhoneComponent } from '@shared/form-elements/input/input-phone/input-phone.component';
import { InputComponent } from '@shared/form-elements/input/input/input.component';
import { InputEmailComponent } from '@shared/form-elements/input/input-email/input-email.component';
import { HandleInvalidOnSubmitDirective } from '@shared/form-elements/form-helpers/handle-invalid-on-submit.directive';
import { AsyncPipe, NgIf } from '@angular/common';
import { LoadingDotsComponent } from '@shared/loading-dots/loading-dots.component';

@UntilDestroy()
@Component({
    selector: 'app-reserve-in-store-form',
    templateUrl: './reserve-in-store-form.component.html',
    styleUrls: ['./reserve-in-store-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingDotsComponent, NgIf, FormsModule, HandleInvalidOnSubmitDirective, ReactiveFormsModule, InputEmailComponent, InputComponent, InputPhoneComponent, CheckboxComponent, InterceptLinksDirective, AsyncPipe, SafePipe, TranslateSafePipe, FindStoreFromId]
})
export class ReserveInStoreFormComponent implements OnInit {
    public reserveInStore?: ReserveInStore;

    public form: UntypedFormGroup;
    public quantity: UntypedFormControl;
    public submitting: boolean = false;

    @Output() updateEvent: EventEmitter<ReserveInStoreEvent> = new EventEmitter();

    constructor(
        public readonly settingsService: SettingsService,
        public readonly storesService: StoresService,
        private readonly cd: ChangeDetectorRef,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly customValidatorsService: CustomValidatorsService,
        private readonly notificationService: NotificationService,
        private readonly reserveInStoreService: ReserveInStoreService
    ) {
        this.reserveInStoreService.reserveInStore$
            .pipe(untilDestroyed(this))
            .subscribe(reserveInStore => (this.reserveInStore = reserveInStore));
    }

    ngOnInit(): void {
        // MaxLength validation on inputs is purely to match the validation set up by BE.
        this.form = this.formBuilder.group({
            customerEmail: [
                this.reserveInStore?.customerEmail || '',
                [Validators.required, Validators.maxLength(250), this.customValidatorsService.email]
            ],
            customerName: [this.reserveInStore?.customerName || '', [Validators.required, Validators.maxLength(250)]],
            customerMobile: [
                this.reserveInStore?.customerMobile || '',
                [Validators.required, Validators.maxLength(50), this.customValidatorsService.phoneNumber]
            ],
            customerConsent: [false, Validators.requiredTrue]
        });

        this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(v => {
            this.reserveInStoreService.updateUserInfo(v);
        });
    }

    public onSubmit(): void {
        if (!this.form.valid) {
            // TODO: Add proper handling, where each field that is missing, gets listed in the notification. KOP-615
            this.notificationService.createNotification({
                translationText: 'forms.validationErrors.invalidForm'
            });
            return;
        }

        this.submitting = true;

        this.reserveInStoreService
            .makeReservation(this.form.value)
            .pipe(
                debounceTime(5000),
                take(1),
                untilDestroyed(this),
                finalize(() => {
                    this.submitting = false;
                    this.cd.markForCheck();
                })
            )
            .subscribe(
                ({ orderKey, orderNumber }) => {
                    this.updateEvent.emit({ event: 'CREATED', result: { orderKey, orderNumber } });
                },
                err => {
                    this.updateEvent.emit({ event: 'ERROR', error: err.error });
                }
            );
    }
}
